import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  BriefcaseMedical,
  Globe,
  Shield,
  Stethoscope,
  ScanHeart,
  LogIn,
  Clock,
  CheckCircle2,
  Zap,
  FlaskConical,
  Star,
} from 'lucide-react';
import lumosyeLogoLight from '../assets/Lumosye-logo.png';
import lumosyeLogoDark from '../assets/Lumosye-logo-dark.png';
import lumochartIconLight from '../assets/lumochart-login.png';
import lumochartIconDark from '../assets/lumochart-login-dark.png';
import lumodxIconLight from '../assets/lumodx-logo.png';
import lumodxIconDark from '../assets/lumodx-logo-dark.png';

/* ─────────────────────────── DIVISION CARD ─────────────────────────── */
function DivisionCard({
  image,
  icon,
  eyebrow,
  title,
  description,
  link,
  buttonText,
  status,
  featured,
  loginLink,
}) {
  const isComingSoon = status === 'development';
  const isLive = status === 'live';

  return (
    <div
      className={`relative flex flex-col rounded-[2rem] border transition-all duration-300 overflow-hidden
      ${
        featured
          ? 'border-amber-300/60 dark:border-amber-400/30 shadow-2xl shadow-amber-400/5 hover:-translate-y-2'
          : 'border-gray-200 dark:border-zinc-800 hover:-translate-y-1 hover:shadow-xl'
      }
      bg-white dark:bg-zinc-900`}
    >
      {featured && (
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-amber-400/0 via-amber-400 to-amber-400/0" />
      )}

      <div className="absolute top-5 right-5 flex gap-2">
        {featured && (
          <span className="flex items-center gap-1 rounded-full bg-amber-50 dark:bg-amber-400/10 border border-amber-200 dark:border-amber-400/25 px-2.5 py-1 text-xs font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wider">
            <Star size={10} className="fill-current" />
            Featured
          </span>
        )}

        {isComingSoon && (
          <span className="flex items-center gap-1 rounded-full bg-slate-100 dark:bg-zinc-800 border border-slate-200 dark:border-zinc-700 px-2.5 py-1 text-xs font-semibold text-slate-500 dark:text-zinc-400 uppercase tracking-wider">
            <FlaskConical size={10} />
            In Development
          </span>
        )}

        {isLive && !featured && (
          <span className="flex items-center gap-1.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Live
          </span>
        )}
      </div>

      <div className="p-8 md:p-10 flex flex-col flex-1">
        <div className="flex flex-col items-center text-center mb-6">
          {image ? (
            <img
              src={image}
              alt={title}
              className="h-20 w-auto md:h-28 mb-6 object-contain transition-transform duration-300 hover:scale-[1.03]"
            />
          ) : (
            <div
              className={`h-20 w-20 md:h-24 md:w-24 mb-6 rounded-3xl flex items-center justify-center shadow-inner
              ${featured ? 'bg-amber-50 dark:bg-amber-400/10' : 'bg-gray-100 dark:bg-zinc-800'}`}
            >
              {icon}
            </div>
          )}

          <p
            className={`text-xs font-bold uppercase tracking-[0.25em] mb-2
            ${featured ? 'text-amber-600 dark:text-amber-400' : 'text-slate-500 dark:text-zinc-400'}`}
          >
            {eyebrow}
          </p>

          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3 leading-tight">
            {title}
          </h3>

          <p className="text-base leading-relaxed text-gray-600 dark:text-gray-400">
            {description}
          </p>
        </div>

        <div className="flex-1" />

        <div className="mt-6 flex flex-col gap-3">
          {!isComingSoon && link && (
            <Link
              to={link}
              className={`inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition shadow
                ${
                  featured
                    ? 'bg-amber-400 hover:bg-amber-300 text-amber-950 shadow-amber-400/20'
                    : 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:opacity-90'
                }`}
            >
              {buttonText}
              <ArrowRight size={15} />
            </Link>
          )}

          {loginLink && isLive && (
            <Link
              to={loginLink}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm border border-gray-200 dark:border-zinc-700 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-zinc-500 transition"
            >
              <LogIn size={14} />
              Log in to {title}
            </Link>
          )}

          {isComingSoon && link && (
            <Link
              to={link}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-semibold border border-gray-200 dark:border-zinc-700 text-gray-700 dark:text-zinc-300 hover:border-gray-300 dark:hover:border-zinc-600 transition"
            >
              {buttonText}
              <ArrowRight size={14} />
            </Link>
          )}

          {isComingSoon && !link && (
            <div className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-medium border border-dashed border-slate-200 dark:border-zinc-700 text-slate-400 dark:text-zinc-500 cursor-default select-none">
              <Clock size={14} />
              Coming soon
            </div>
          )}
        </div>
      </div>

      {featured && (
        <div className="px-10 pb-6 pt-0">
          <div className="rounded-xl bg-amber-50 dark:bg-amber-400/5 border border-amber-100 dark:border-amber-400/15 px-4 py-3 flex items-center gap-2.5">
            <BriefcaseMedical size={14} className="text-amber-600 dark:text-amber-400 shrink-0" />
            <p className="text-xs text-amber-700 dark:text-amber-300/80 leading-snug">
              Physician-run · Available now for hospitals & clinicians
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── PILLAR CARD ───────────────────────────── */
function PillarCard({ icon, title, text }) {
  return (
    <div className="rounded-2xl border border-gray-100 dark:border-zinc-800 bg-white dark:bg-zinc-900/60 p-6 hover:border-gray-300 dark:hover:border-zinc-600 transition duration-300">
      <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gray-50 dark:bg-zinc-800">
        {icon}
      </div>
      <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">{title}</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{text}</p>
    </div>
  );
}

/* ─────────────────────────── MAIN PAGE ─────────────────────────────── */
export default function OrganizationLanding() {
  const [scrollY, setScrollY] = useState(0);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [headerVisible, setHeaderVisible] = useState(true);
  const [showLoginFloat, setShowLoginFloat] = useState(false);
  const heroRef = useRef(null);

  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    if (saved !== null) return JSON.parse(saved);
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    const root = document.documentElement;
    const updateDarkMode = () => {
      const dark = root.classList.contains('dark');
      setIsDark(dark);
      localStorage.setItem('darkMode', JSON.stringify(dark));
    };
    const observer = new MutationObserver(updateDarkMode);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    updateDarkMode();
    return () => observer.disconnect();
  }, []);

  const toggleDarkMode = () => {
    const root = document.documentElement;
    root.classList.toggle('dark');
    const dark = root.classList.contains('dark');
    setIsDark(dark);
    localStorage.setItem('darkMode', JSON.stringify(dark));
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      setHeaderVisible(currentY < lastScrollY || currentY < 80);
      setScrollY(currentY);
      setLastScrollY(currentY);
      setShowLoginFloat(currentY > 500);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const lumosyeLogo = isDark ? lumosyeLogoDark : lumosyeLogoLight;
  const lumochartIcon = isDark ? lumochartIconDark : lumochartIconLight;
  const lumodxIcon = isDark ? lumodxIconDark : lumodxIconLight;

  const headerOpacity = scrollY < 300 ? 1 - scrollY / 600 : 0.45;
  const headerBlur = scrollY < 300 ? 10 + scrollY / 60 : 14;

  return (
    <div className="bg-[#f8f9fb] dark:bg-[#080c14] text-gray-800 dark:text-gray-100 min-h-screen relative transition-colors duration-300">
      {/* background texture */}
      <div
        className="fixed inset-0 pointer-events-none z-0 opacity-[0.018] dark:opacity-[0.03]"
        style={{
          backgroundImage: 'radial-gradient(circle, #64748b 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      />

      {/* ambient glow */}
      <div
        className="fixed top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] pointer-events-none z-0"
        style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(59,130,246,0.06) 0%, transparent 70%)' }}
      />

      {/* quick login float */}
      <div
        className={`fixed bottom-6 right-6 z-50 transition-all duration-500 ${
          showLoginFloat ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
      >
        <Link
          to="/login"
          className="group flex items-center gap-3 rounded-2xl border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-4 py-3 shadow-xl shadow-black/10 dark:shadow-black/40 hover:shadow-2xl hover:border-blue-300 dark:hover:border-blue-500/50 transition-all duration-300"
        >
          <img src={lumochartIcon} alt="LumoChart" className="h-7 w-auto object-contain" />
          <div className="flex flex-col leading-tight">
            <span className="text-xs text-gray-400 dark:text-zinc-500">Quick access</span>
            <span className="text-sm font-bold text-gray-900 dark:text-white">Log in to LumoChart</span>
          </div>
          <LogIn size={15} className="text-gray-400 dark:text-zinc-500 group-hover:text-blue-500 transition ml-1" />
        </Link>
      </div>

      {/* header */}
      <header
        className={`fixed top-0 w-full z-50 transition-all duration-500 ${
          headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
        }`}
        style={{
          backgroundColor: isDark
            ? `rgba(8,12,20,${headerOpacity * 0.98})`
            : `rgba(248,249,251,${headerOpacity * 0.95})`,
          backdropFilter: `blur(${headerBlur}px)`,
          borderBottom: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.06)',
        }}
      >
        <nav className="container mx-auto px-6 py-4 md:py-5 flex justify-between items-center max-w-7xl">
          <Link to="/" className="flex items-center">
            <img
              src={lumosyeLogo}
              alt="Lumosye Health"
              className="h-12 md:h-14 w-auto object-contain transition-transform duration-300 hover:scale-[1.03]"
            />
          </Link>

          <div className="hidden md:flex items-center gap-7 text-sm font-medium text-gray-600 dark:text-gray-400">
            <a href="#about" className="hover:text-gray-900 dark:hover:text-white transition">About</a>
            <a href="#divisions" className="hover:text-gray-900 dark:hover:text-white transition">Divisions</a>
            <a href="#contact" className="hover:text-gray-900 dark:hover:text-white transition">Contact</a>

            <div className="h-4 w-px bg-gray-200 dark:bg-zinc-700" />

            <Link
              to="/login"
              className="flex items-center gap-1.5 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition"
            >
              <LogIn size={14} />
              LumoChart Login
            </Link>

            <button
              onClick={toggleDarkMode}
              className="text-xs bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-gray-300 px-3 py-1.5 rounded-full hover:scale-105 transition border border-gray-200 dark:border-zinc-700"
            >
              {isDark ? '☀️ Light' : '🌙 Dark'}
            </button>
          </div>
        </nav>
      </header>

      {/* hero */}
      <section ref={heroRef} className="relative z-10 pt-36 md:pt-48 pb-24 md:pb-32 text-center">
        <div className="container mx-auto px-6 max-w-5xl">
          <h1 className="text-5xl md:text-7xl lg:text-[5.5rem] font-extrabold text-gray-900 dark:text-white leading-[0.95] tracking-tight mb-7">
            Clinical tools
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-400 dark:from-blue-400 dark:to-sky-300">
              built by clinicians.
            </span>
          </h1>

          <p className="text-lg md:text-xl text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed mb-10">
            Lumosye Health develops premium solutions across clinical documentation, diagnostic
            intelligence, and physician-run medical staffing — designed around how care actually works.
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-3 mb-16">
            <a
              href="#divisions"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-8 py-3.5 text-base font-bold hover:opacity-90 transition shadow-xl"
            >
              Explore Divisions
              <ArrowRight size={16} />
            </a>
            <Link
              to="/medical-staffing"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-gray-800 dark:text-gray-200 px-8 py-3.5 text-base font-semibold hover:border-gray-400 dark:hover:border-zinc-500 transition shadow-md"
            >
              Medical Staffing
              <ArrowUpRight size={16} />
            </Link>
          </div>

          <div className="inline-flex items-center gap-4 rounded-2xl border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 px-5 py-3.5 shadow-md">
            <img src={lumochartIcon} alt="LumoChart" className="h-7 w-auto object-contain" />
            <div className="text-left">
              <p className="text-xs text-gray-400 dark:text-zinc-500 leading-none mb-0.5">Already using LumoChart?</p>
              <p className="text-sm font-bold text-gray-900 dark:text-white leading-none">Access your notes directly</p>
            </div>
            <Link
              to="/login"
              className="ml-2 inline-flex items-center gap-1.5 rounded-full bg-blue-600 dark:bg-blue-500 text-white px-4 py-2 text-xs font-bold hover:bg-blue-700 dark:hover:bg-blue-400 transition shadow"
            >
              <LogIn size={12} />
              Log In
            </Link>
          </div>
        </div>
      </section>

      {/* pillars */}
      <section id="about" className="relative z-10 py-20 md:py-24 border-t border-gray-100 dark:border-zinc-900 bg-white dark:bg-zinc-950/60">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-gray-400 dark:text-zinc-500 mb-3">
              What We Build
            </p>
            <h2 className="text-2xl md:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight">
              Three pillars. One mission.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            <PillarCard
              icon={<Stethoscope className="text-blue-500" size={22} strokeWidth={1.8} />}
              title="Clinical Workflow"
              text="Documentation tools that streamline notes, organization, and clinician productivity across care settings."
            />
            <PillarCard
              icon={<ScanHeart className="text-blue-500" size={22} strokeWidth={1.8} />}
              title="Diagnostic Intelligence"
              text="Technology designed to support faster, smarter diagnostic workflows in complex clinical environments."
            />
            <PillarCard
              icon={<BriefcaseMedical className="text-blue-500" size={22} strokeWidth={1.8} />}
              title="Medical Staffing"
              text="Physician-run locum placement for hospitals and systems that need dependable, flexible coverage."
            />
          </div>
        </div>
      </section>

      {/* divisions */}
      <section id="divisions" className="relative z-10 py-24 md:py-32 border-t border-gray-100 dark:border-zinc-900">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="flex items-end justify-between mb-14">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-gray-400 dark:text-zinc-500 mb-3">
                Our Divisions
              </p>
              <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight">
                Products &<br />Services
              </h2>
            </div>
            <p className="hidden lg:block text-sm text-gray-400 dark:text-zinc-500 max-w-xs text-right leading-relaxed">
              Two clinical software products and a physician-run staffing division.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <DivisionCard
              image={lumochartIcon}
              eyebrow="Clinical Documentation"
              title="LumoChart"
              description="AI-powered clinical documentation that helps providers generate structured, high-quality notes across inpatient, emergency, and outpatient workflows."
              link="/lumochart"
              buttonText="Visit LumoChart"
              loginLink="/login"
              status="live"
            />

            <DivisionCard
              eyebrow="Medical Staffing"
              title="Lumosye Medical Staffing"
              icon={<BriefcaseMedical size={36} className="text-amber-600 dark:text-amber-400" strokeWidth={1.6} />}
              description="A physician-run locum tenens service connecting hospitals with hospitalists, emergency physicians, and APPs — with licensing support, malpractice coordination, and nationwide reach."
              link="/medical-staffing"
              buttonText="View Staffing Division"
              status="live"
              featured={true}
            />

            <DivisionCard
              image={lumodxIcon}
              eyebrow="Diagnostic Intelligence"
              title="LumoDx"
              description="An intelligent imaging and diagnostics companion designed to enhance clinical interpretation, workflow efficiency, and real-time decision support."
              link="/lumodx"
              buttonText="Learn More"
              status="development"
            />
          </div>
        </div>
      </section>

      {/* trust band */}
      <section
        id="trust"
        className="relative z-10 py-20 border-t border-gray-100 dark:border-zinc-900 bg-white dark:bg-zinc-950/60"
      >
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-gray-400 dark:text-zinc-500 mb-4">
                Our Commitment
              </p>
              <h2 className="text-2xl md:text-4xl font-extrabold text-gray-900 dark:text-white mb-4 leading-tight">
                Built for real
                <br />
                healthcare environments.
              </h2>
              <p className="text-gray-500 dark:text-gray-400 leading-relaxed">
                Every product and service at Lumosye Health is built with privacy-conscious design,
                operational reliability, and clinician-centered execution — across every division.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: <Shield size={20} className="text-blue-500" />, label: 'Privacy-first design', sub: 'Built for HIPAA-sensitive environments' },
                { icon: <Globe size={20} className="text-blue-500" />, label: 'Nationwide reach', sub: 'Serving facilities across the US' },
                { icon: <CheckCircle2 size={20} className="text-blue-500" />, label: 'Physician-run', sub: 'Clinician-led operating model' },
                { icon: <Zap size={20} className="text-blue-500" />, label: 'Built to scale', sub: 'Growing clinical network' },
              ].map(({ icon, label, sub }) => (
                <div key={label} className="rounded-xl border border-gray-100 dark:border-zinc-800 bg-gray-50 dark:bg-zinc-900 p-4">
                  <div className="mb-2">{icon}</div>
                  <p className="text-sm font-bold text-gray-900 dark:text-white mb-0.5">{label}</p>
                  <p className="text-xs text-gray-400 dark:text-zinc-500 leading-snug">{sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* contact */}
      <section id="contact" className="relative z-10 py-24 md:py-32 border-t border-gray-100 dark:border-zinc-900">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-gray-400 dark:text-zinc-500 mb-5">
            Get In Touch
          </p>
          <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 dark:text-white mb-5 leading-tight">
            Questions, partnerships,
            <br />
            or coverage requests?
          </h2>
          <p className="text-lg text-gray-500 dark:text-gray-400 max-w-2xl mx-auto mb-10">
            For product inquiries, clinical staffing requests, collaborations, or general support
            — reach our team directly.
          </p>
          <a
            href="mailto:contact@lumosye.com?subject=Lumosye%20Inquiry"
            className="inline-flex items-center gap-2.5 rounded-full bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-9 py-4 text-base font-bold hover:opacity-90 transition shadow-xl"
          >
            contact@lumosye.com
            <ArrowRight size={16} />
          </a>
        </div>
      </section>

      {/* footer */}
      <footer className="relative z-10 border-t border-gray-100 dark:border-zinc-900 bg-white dark:bg-zinc-950 py-10">
        <div className="container mx-auto px-6 max-w-7xl flex flex-col md:flex-row items-center justify-between gap-5">
          <div className="flex items-center gap-3">
            <img src={lumosyeLogo} alt="Lumosye" className="h-8 w-auto opacity-70" />
            <span className="text-xs text-gray-400 dark:text-zinc-600">Lumosye Health Services, LLC</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6 text-xs text-gray-400 dark:text-zinc-600">
            <Link to="/privacy" className="hover:text-gray-700 dark:hover:text-zinc-400 transition">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-gray-700 dark:hover:text-zinc-400 transition">Terms of Use</Link>
            <Link to="/support" className="hover:text-gray-700 dark:hover:text-zinc-400 transition">Support</Link>
            <span>© {new Date().getFullYear()} Lumosye Health Services, LLC</span>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.04); }
        }
        .animate-pulse-slow { animation: pulse-slow 8s ease-in-out infinite; }
      `}</style>
    </div>
  );
}